// Growem - validacion de la captura de series (peso, repeticiones, RIR, segundos).
//
// POR QUE EXISTE (auditoria de recalibracion, Fase 1B):
// el parseo de los campos de Sesion vivia DUPLICADO en dos sitios (onFld y onWeightBlur) y solo
// filtraba NaN: cualquier numero entraba al log. Un dedazo (900 kg, 51 reps, RIR 12) se guardaba
// como dato real, entraba al indice de fuerza como e1RM valido y ademas apilaba una entrada de
// undo. El indice ya clampa el PASO (±ln 1.30), pero clampar no borra el dato erroneo: la unica
// defensa barata es no dejarlo entrar. Este modulo es la FUENTE UNICA de esos limites y del
// parseo, para que los dos canales de escritura decidan exactamente igual.
//
// CONSCIENTE DEL CAMPO (obligatorio, no es un adorno): en los ejercicios por tiempo (plancha,
// farmer carry) el log guarda los SEGUNDOS en el mismo campo `r` que las repeticiones
// (Sesion.tsx, fila de la serie). Un tope ciego de 50 sobre `r` habria roto esa captura, asi que
// el canal de segundos se valida con su propio rango (1..3600) bajo el campo logico 'sec'.
//
// TRES RESULTADOS QUE NO SON ERROR:
//  - 'empty'   : el campo quedo vacio. Devuelve value undefined y el llamador escribe undefined,
//                JAMAS 0 (un 0 es un dato: 0 reps o RIR 0 significan algo muy distinto a "sin dato").
//  - 'pending' : el usuario va a medio teclear un decimal ("31,"). Ni se marca error ni se escribe:
//                gritarle a quien esta escribiendo 31,5 es el peor momento posible.
//  - 'ok'      : hay un numero valido dentro de los limites.
// El cuarto, 'invalid', trae un codigo (`reason`) para las pruebas y un `message` en espanol llano
// que dice el rango valido y avisa que el dato NO quedo guardado.

/** Campos capturables. 'sec' no es un campo del log: es el canal de segundos, que persiste en `r`. */
export type CaptureField = 'w' | 'r' | 'rir' | 'sec'

/** Codigos de rechazo (estables para pruebas y telemetria futura; el texto puede cambiar). */
export type CaptureReason = 'nan' | 'whole' | 'range'

export type CaptureResult =
  | { ok: true; status: 'ok'; value: number }
  | { ok: true; status: 'empty'; value?: undefined }
  | { ok: true; status: 'pending'; value?: undefined }
  | { ok: false; status: 'invalid'; reason: CaptureReason; message: string }

/**
 * Limites 5b (auditados). El peso admite decimales (discos de 1,25 kg y mancuernas fraccionadas);
 * repeticiones, RIR y segundos son cuentas enteras. El minimo del peso es EXCLUSIVO: 0 kg no es
 * una serie con carga, es un campo mal escrito (y el 0 rompe la cascada de peso de abajo).
 * 500 kg supera cualquier marca humana registrada en estos movimientos; 50 reps y 3600 s son topes
 * generosos que solo atrapan dedazos. RIR llega a 6 (no a 4 como el clamp del indice) porque el
 * usuario puede reportar honestamente 5 o 6 en una serie muy suave; el indice ya lo acota aparte.
 */
export const CAPTURE_LIMITS = {
  w: { min: 0, max: 500, minExclusive: true, integer: false },
  r: { min: 1, max: 50, minExclusive: false, integer: true },
  rir: { min: 0, max: 6, minExclusive: false, integer: true },
  sec: { min: 1, max: 3600, minExclusive: false, integer: true },
} as const satisfies Record<CaptureField, { min: number; max: number; minExclusive: boolean; integer: boolean }>

// Mensajes: espanol llano, sin jerga nueva y sin rayas em/en (regla del proyecto). Todos cierran
// avisando que el dato no quedo guardado, porque el campo CONSERVA lo tecleado y sin ese aviso el
// usuario creeria que 900 kg entro al registro.
// PRESUPUESTO DE ANCHO (medido sobre el CSS real, no a ojo): a 390 px el #view deja 354, la card
// 320 y la fila .set 294; el campo de RIR es el ultimo de la fila, asi que su mensaje (una linea de
// 10.5 px, centrado bajo el campo) solo cabe si no pasa de ~28 caracteres. De ahi que el rango se
// diga por el limite VIOLADO (minimo o maximo) y no como intervalo: sale mas corto y mas exacto
// (decir "de 0 a 500" cuando el 0 esta prohibido seria mentira).
const NAN_MSG = 'Solo números. No se guardó.'

const MIN_MSG: Record<CaptureField, string> = {
  w: 'Peso mayor que 0. No se guardó.',
  r: 'Mínimo 1 repetición. No se guardó.',
  rir: 'RIR mínimo 0. No se guardó.',
  sec: 'Mínimo 1 segundo. No se guardó.',
}

const MAX_MSG: Record<CaptureField, string> = {
  w: 'Peso máximo 500 kg. No se guardó.',
  r: 'Máximo 50 repeticiones. No se guardó.',
  rir: 'RIR máximo 6. No se guardó.',
  sec: 'Máximo 3600 segundos. No se guardó.',
}

const WHOLE_MSG: Partial<Record<CaptureField, string>> = {
  r: 'Repeticiones enteras. No se guardó.',
  rir: 'RIR entero. No se guardó.',
  sec: 'Segundos enteros. No se guardó.',
}

// Solo digitos con un separador decimal opcional. Deliberadamente MAS estricto que Number(): sin
// exponente, sin hexadecimal, sin signo mas, sin espacios internos, sin Infinity. Number('0x10')
// vale 16 y Number('1e3') vale 1000; ambos entrarian como pesos "validos" por la puerta de atras.
const NUMERIC = /^\d+(?:[.,]\d+)?$/
// Negativo bien formado: es un numero, el problema es el rango, asi que merece el mensaje de rango.
const NEGATIVE = /^-\d+(?:[.,]\d+)?$/
// Decimal a medio teclear: "", "31,", ",". No es un error, es alguien escribiendo.
const PENDING = /^\d*[.,]$/

/**
 * Parsea lo tecleado en un campo de la serie. Puro: ni reloj, ni store, ni DOM.
 * Nunca lanza. El llamador escribe al log SOLO con status 'ok' (valor) o 'empty' (undefined).
 */
export function parseCapture(field: CaptureField, raw: string): CaptureResult {
  const t = (raw ?? '').trim()
  if (t === '') return { ok: true, status: 'empty' }
  if (PENDING.test(t)) return { ok: true, status: 'pending' }

  const lim = CAPTURE_LIMITS[field]
  const invalid = (reason: CaptureReason, message: string): CaptureResult =>
    ({ ok: false, status: 'invalid', reason, message })

  // Un negativo SI es un numero: el problema es el rango, asi que merece el mensaje del minimo.
  if (NEGATIVE.test(t)) return invalid('range', MIN_MSG[field])
  if (!NUMERIC.test(t)) return invalid('nan', NAN_MSG)

  const n = Number(t.replace(',', '.')) // es-CO escribe los decimales con coma (31,5)
  if (!Number.isFinite(n)) return invalid('nan', NAN_MSG) // defensa: NUMERIC ya lo garantiza

  if (lim.integer && !Number.isInteger(n)) return invalid('whole', WHOLE_MSG[field] ?? NAN_MSG)

  const belowMin = lim.minExclusive ? !(n > lim.min) : n < lim.min
  if (belowMin) return invalid('range', MIN_MSG[field])
  if (n > lim.max) return invalid('range', MAX_MSG[field])

  return { ok: true, status: 'ok', value: n }
}

/**
 * Segundos del cronometro de Sesion (tercer canal de escritura, junto al tecleo y al blur de peso).
 * SF4/SF17: el cronometro no puede "rechazar y avisar" como el tecleo (el usuario no escribio nada,
 * solo pulso detener), asi que aqui se CLAMPA a los mismos limites de 'sec' en vez de descartar:
 *  - por abajo a 1 s: una pulsacion instantanea sigue siendo una serie intentada, no un 0;
 *  - por arriba a 3600 s: una medicion de mas de 1 hora no es una serie real, es un cronometro
 *    olvidado en marcha; guardar el tope es mas honesto que guardar un dato imposible.
 * Vive aqui (y no en Sesion.tsx) para que capture.ts siga siendo la FUENTE UNICA de los limites:
 * si un dia cambia CAPTURE_LIMITS.sec, el cronometro cambia solo.
 */
export function clampStopwatchSeconds(elapsedMs: number): number {
  const lim = CAPTURE_LIMITS.sec
  return Math.min(lim.max, Math.max(lim.min, Math.round(elapsedMs / 1000)))
}
